import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { BrandRow } from '../../../../lib/supabase';
import BrandFormModal from '../../brands/components/BrandFormModal';

export default function BrandsPanel() {
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState<BrandRow | null | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('agency_brands')
      .select('*')
      .order('sort_order', { ascending: true });
    setBrands(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    await supabase.from('agency_brands').delete().eq('id', deleteId);
    setDeleteId(null);
    setDeleting(false);
    fetchBrands();
  };

  return (
    <>
      {/* Action Bar */}
      <div className="bg-white border border-gray-100 p-4 mb-4 flex items-center justify-between gap-3">
        <span className="text-xs text-gray-400">共 {brands.length} 個代理品牌</span>
        <button
          onClick={() => setEditTarget(null)}
          className="flex items-center gap-1.5 bg-[#1a73b5] text-white text-sm px-4 py-2 hover:bg-[#1558a0] transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base" />
          </div>
          新增品牌
        </button>
      </div>

      {/* Brand Table */}
      <div className="bg-white border border-gray-100 overflow-x-auto">
        {loading ? (
          <div className="py-16 text-center">
            <i className="ri-loader-4-line text-3xl text-gray-300 animate-spin block mb-2" />
            <p className="text-sm text-gray-400">載入中...</p>
          </div>
        ) : brands.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <i className="ri-building-line text-5xl text-gray-200" />
            </div>
            <p className="text-sm text-gray-400">尚未新增任何代理品牌</p>
            <button
              onClick={() => setEditTarget(null)}
              className="mt-3 text-xs text-[#1a73b5] hover:underline cursor-pointer"
            >
              + 新增第一個品牌
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-[#f8f9fb]">
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3 w-24">Logo</th>
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3">品牌名稱</th>
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3 hidden md:table-cell">品牌簡介</th>
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3 w-16">排序</th>
                <th className="text-left text-xs font-medium text-gray-400 px-4 py-3 w-20">狀態</th>
                <th className="text-right text-xs font-medium text-gray-400 px-4 py-3 w-24">操作</th>
              </tr>
            </thead>
            <tbody>
              {brands.map(brand => (
                <tr key={brand.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="w-20 h-14 border border-gray-100 bg-white flex items-center justify-center overflow-hidden">
                      {brand.logo_url ? (
                        <img
                          src={brand.logo_url}
                          alt={brand.name}
                          className="max-w-full max-h-full object-contain p-1"
                        />
                      ) : (
                        <i className="ri-image-line text-gray-200 text-xl" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-700 text-sm leading-snug">{brand.name}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{brand.description}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-500 font-mono">{brand.sort_order}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 ${
                      brand.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <i className={`${brand.is_active ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'} text-xs`} />
                      {brand.is_active ? '顯示' : '隱藏'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditTarget(brand)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-[#1a73b5] hover:bg-[#f0f6ff] transition-colors cursor-pointer"
                        title="編輯"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={() => setDeleteId(brand.id)}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        title="刪除"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info tip */}
      <div className="mt-4 flex items-start gap-2 bg-[#f0f6ff] border border-[#dde8f5] px-4 py-3">
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-information-line text-[#1a73b5] text-sm" />
        </div>
        <p className="text-xs text-[#1a73b5] leading-relaxed">
          代理品牌的更動會直接反映在前台「關於我們」頁面的代理品牌區塊。可透過「排序」欄位調整品牌顯示順序（數字越小越前面）。
        </p>
      </div>

      {/* Form Modal */}
      {editTarget !== undefined && (
        <BrandFormModal
          brand={editTarget}
          onClose={() => setEditTarget(undefined)}
          onSaved={fetchBrands}
        />
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
          onClick={() => setDeleteId(null)}
        >
          <div className="bg-white p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-10 mx-auto mb-4 flex items-center justify-center bg-red-50">
              <i className="ri-delete-bin-line text-red-400 text-xl" />
            </div>
            <h4 className="text-base font-semibold text-gray-800 text-center mb-2">確認刪除品牌？</h4>
            <p className="text-sm text-gray-500 text-center mb-5">刪除後前台代理品牌區塊將不再顯示此品牌，無法恢復。</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 py-2 text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2 text-sm bg-red-500 text-white hover:bg-red-600 cursor-pointer disabled:opacity-60 whitespace-nowrap"
              >
                {deleting ? '刪除中...' : '確認刪除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
